import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import useAutocomplete from '@mui/material/useAutocomplete';
import { useEffect, useMemo, useState } from 'react';
import type { HTMLAttributes, ReactNode } from 'react';

import type { DropdownItemBase } from '@/components/Autocomplete/autocomplete.types';
import { AutocompleteDropdownOptions } from '@/components/Autocomplete/AutocompleteDropdownOptions/AutocompleteDropdownOptions';
import { AutocompleteInput } from '@/components/Autocomplete/AutocompleteInput/AutocompleteInput';
import {
	buildSectionHeaders,
	flattenSections,
	type SectionGroup
} from '@/components/Autocomplete/sectionUtils';
import { AddFormFieldButton } from '@/components/Buttons/AddFormFieldButton/AddFormFieldButton';
import { RemoveFormFieldButton } from '@/components/Buttons/RemoveFormFieldButton/RemoveFormFieldButton';
import { HcoSection } from '@/pages/MyDCRsPage/ViewDCRModal/_hooks/useHcoFieldNames';
import { HcpSection } from '@/pages/MyDCRsPage/ViewDCRModal/_hooks/useHcpFieldNames';
import { formatLocalizedName } from '@/utils/formatters/formatLocalizedName';
import type { RequirePair } from '@/utils/typescript/RequirePair';

import { FormReadOnlyField } from '../FormReadOnlyField/FormReadOnlyField';

import { formAutocompleteFieldStyles as sx } from './FormAutocompleteField.styles';

type BaseProps<T> = {
	label: string;
	loading?: boolean;
	placeholder?: string;
	indicateError?: boolean;
	isDirty?: boolean;
	defaultValue?: string | null | undefined;
	disabled?: boolean;
	isReadOnly?: boolean;
	isRequired?: boolean;
	name?: string;
	section: HcoSection | HcpSection;
	id?: string;
	primaryRadio?: ReactNode;
	isAsync?: boolean;
	renderOption?: (
		option: T,
		optionProps: HTMLAttributes<HTMLLIElement>
	) => ReactNode;
	onChange?: (v: string) => void;
	handleOnDropdownItemSelect: (item: T | null) => void;
	hideLabel?: boolean;
	caption?: ReactNode;
	size?: 'small' | 'medium';
} & RequirePair<'canAddNewItem', 'onAddNewItemClick', VoidFunction> &
	RequirePair<'canBeRemoved', 'onRemove', () => void>;

type Props<T> = BaseProps<T> &
	(
		| { options: Array<T> | null; sectionedOptions?: never }
		| { options?: never; sectionedOptions: SectionGroup<T>[] }
	);

export const FormAutocompleteField = <
	T extends DropdownItemBase = DropdownItemBase
>({
	options,
	sectionedOptions,
	loading = false,
	handleOnDropdownItemSelect,
	label,
	placeholder,
	defaultValue,
	disabled,
	name,
	id,
	canBeRemoved,
	canAddNewItem,
	indicateError,
	isReadOnly,
	isDirty,
	onRemove,
	onAddNewItemClick,
	onChange,
	renderOption,
	primaryRadio,
	hideLabel = false,
	isRequired = false,
	isAsync = false,
	section,
	caption,
	size = 'medium'
}: Props<T>) => {
	const [query, setQuery] = useState('');

	const { flatOptions, sectionIdxMap, getRealId } = useMemo(() => {
		if (!sectionedOptions)
			return {
				flatOptions: undefined,
				sectionIdxMap: undefined,
				getRealId: undefined
			};
		return flattenSections(sectionedOptions);
	}, [sectionedOptions]);

	const effectiveOptions = flatOptions ?? options;

	const [selected, setSelected] = useState<T | null>(() => {
		if (!defaultValue || !effectiveOptions) return null;
		return (
			effectiveOptions.find(
				opt =>
					(getRealId ? getRealId(opt.id) : opt.id) === defaultValue ||
					opt.name === defaultValue
			) ?? null
		);
	});

	// TODO (Ben): Refactor the defaultValue behaviour. I don't like this as it requires the option in the list to display the default value.
	// It would be better if the defaultValue could just be a string that is defined at the parent level.
	useEffect(() => {
		if (!defaultValue || !effectiveOptions) {
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setSelected(null);
			return;
		}

		const match = effectiveOptions.find(
			opt =>
				(getRealId ? getRealId(opt.id) : opt.id) === defaultValue ||
				opt.name === defaultValue
		);

		if (match) {
			setSelected(prev => (prev?.id === match.id ? prev : match));
		}
	}, [defaultValue, effectiveOptions, getRealId]);

	const {
		getRootProps,
		getInputProps,
		getListboxProps,
		getOptionProps,
		groupedOptions,
		popupOpen,
		setAnchorEl,
		anchorEl
	} = useAutocomplete<T, false>({
		id,
		options: flatOptions ?? options ?? [],
		getOptionLabel: opt => formatLocalizedName(opt),
		value: selected,
		disabled,
		clearOnBlur: !isAsync,
		isOptionEqualToValue: sectionedOptions
			? (option, value) => option === value
			: (option, value) => option.id === value.id,
		onInputChange: (_event, value, reason) => {
			if (reason === 'input' || reason === 'clear') {
				onChange?.(value.trim());
				setQuery(value.trim());
			}
		},
		onChange: (_event, newValue, reason) => {
			if (reason === 'clear') {
				setSelected(null);
				handleOnDropdownItemSelect(null);
				onChange?.('');
				return;
			}
			if (reason === 'selectOption' && newValue) {
				setSelected(newValue);
				const realItem = getRealId
					? ({ ...newValue, id: getRealId(newValue.id) } as T)
					: newValue;
				handleOnDropdownItemSelect(realItem);
			}
		},
		filterOptions: sectionedOptions
			? (opts, { inputValue }) => {
					if (!inputValue) return opts;
					const lower = inputValue.toLowerCase();
					const matched = opts.filter(opt =>
						formatLocalizedName(opt).toLowerCase().includes(lower)
					);
					const seen = new Set<string>();
					return matched.filter(opt => {
						const realId = getRealId ? getRealId(opt.id) : opt.id;
						if (seen.has(realId)) return false;
						seen.add(realId);
						return true;
					});
				}
			: isAsync
				? opts => opts
				: undefined
	});

	const sectionHeaders = useMemo(() => {
		if (!sectionedOptions || !sectionIdxMap) return undefined;
		return buildSectionHeaders(
			groupedOptions as T[],
			sectionedOptions,
			sectionIdxMap,
			query
		);
	}, [groupedOptions, sectionedOptions, sectionIdxMap, query]);

	const open = useMemo(
		() =>
			isAsync
				? popupOpen && query.length > 0 && effectiveOptions !== null
				: popupOpen,
		[isAsync, effectiveOptions, popupOpen, query.length]
	);

	return (
		<Box sx={() => sx.root(!!isReadOnly, !!canAddNewItem, hideLabel)}>
			{isReadOnly && (
				<>
					<FormReadOnlyField
						value={selected?.name || ''}
						label={label}
						name={name || ''}
						section={section}
					/>

					{caption && <Typography sx={sx.caption}>{caption}</Typography>}

					{/* The useAutocomplete hook requires an input element to always be present to attach its ref and event listeners - thus we render a hidden input here to satisfy RHF when the field is read-only */}
					<input {...getInputProps()} style={{ display: 'none' }} />
				</>
			)}

			{!isReadOnly && (
				<>
					{!hideLabel && (
						<Typography component='label' htmlFor={id} sx={sx.label}>
							{label}
						</Typography>
					)}

					<Box>
						<Box sx={sx.fieldWrap}>
							<Box {...getRootProps()} sx={sx.field}>
								<AutocompleteInput<T>
									disabled={disabled}
									getInputProps={getInputProps}
									popupOpen={open}
									setAnchorEl={setAnchorEl}
									name={name}
									placeholder={placeholder ?? label}
									indicateError={indicateError}
									isDirty={isDirty}
									isRequired={isRequired}
									size={size}
								/>

								<Box onMouseDown={e => e.preventDefault()}>
									<AutocompleteDropdownOptions<T>
										open={open}
										loading={loading}
										query={query}
										isAsync={isAsync}
										getListboxProps={getListboxProps}
										getOptionProps={getOptionProps}
										options={options}
										groupedOptions={groupedOptions}
										anchorEl={anchorEl}
										renderOption={renderOption}
										sectionHeaders={sectionHeaders}
									/>
								</Box>
							</Box>

							{(primaryRadio || canBeRemoved) && (
								<Box sx={sx.fieldControls}>
									{primaryRadio}
									{!!canBeRemoved && !!onRemove && (
										<RemoveFormFieldButton
											onClick={onRemove}
											customSx={sx.removeButton(!!isReadOnly)}
										/>
									)}
								</Box>
							)}
						</Box>

						{caption && <Typography sx={sx.caption}>{caption}</Typography>}

						{canAddNewItem && (
							<Box sx={sx.addNewItemButtonWrap}>
								<AddFormFieldButton onClick={onAddNewItemClick} />
							</Box>
						)}
					</Box>
				</>
			)}
		</Box>
	);
};
